/* eslint-disable jira/filename-case, jira/ff/unsafe-no-exposure, jira/naming/disallow-filenames */

import {
	SOFTWARE,
	SERVICE_DESK,
	PRODUCT_DISCOVERY,
} from '@jira/platform__application-editions/src/application-key.tsx';
import type { Product, Edition } from '@atlassian/edition-awareness/types';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { getTrialDaysLeftFromTrialEndTime } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

export const useIsEligibleForUpsellEa = ({
	product,
	edition,
	isSiteAdmin,
}: {
	product: Product | undefined;
	edition: Edition | undefined;
	isSiteAdmin: boolean;
}): {
	isEligibleForUpsellEa: boolean;
	cohort: string;
} => {
	const isJpd = product === PRODUCT_DISCOVERY;

	const [expConfig] = isJpd
		? UNSAFE_noExposureExp('edition_awareness_upsell_jpd')
		: UNSAFE_noExposureExp('edition_awareness_upsell_jira_ab');

	const passesFeatureGate = isJpd
		? fg('upsell_edition_awareness_jpd_fg')
		: fg('edition_awareness_upsell_jira_ab_fg');

	const productEntitlementDetails = useProductEntitlementDetails();
	const cohort = expConfig.get('cohort', 'not-enrolled');
	const enrolled = cohort !== 'not-enrolled';

	// Trial State logic
	let productKey;
	switch (product) {
		case 'jira':
			productKey = SOFTWARE;
			break;
		case 'jira-service-management':
			productKey = SERVICE_DESK;
			break;
		case PRODUCT_DISCOVERY:
			productKey = PRODUCT_DISCOVERY;
			break;
		default:
	}

	const entitlementDetails = productKey && productEntitlementDetails?.[productKey];
	const trialDaysLeft = getTrialDaysLeftFromTrialEndTime(entitlementDetails?.trialEndTime);
	const isInTrial = !entitlementDetails ? undefined : !!trialDaysLeft && trialDaysLeft > 0;

	// Check if site is in pre-dunning period
	// If firstPredunningEndTime exists and current time is before it, site is in pre-dunning
	// If firstPredunningEndTime doesn't exist, site is not in pre-dunning
	const isInPreDunning = !entitlementDetails
		? undefined
		: entitlementDetails.firstPredunningEndTime !== undefined &&
			Date.now() <= entitlementDetails.firstPredunningEndTime;

	const isEligibleForUpsellEa =
		edition === 'standard' &&
		enrolled &&
		isInTrial === false &&
		isInPreDunning === false &&
		isSiteAdmin &&
		passesFeatureGate;

	return {
		isEligibleForUpsellEa,
		cohort,
	};
};
